import { ReactElement, useEffect, useMemo, useRef } from 'react'
import {
    GreenInputField,
    Heading,
    Image,
    SVG,
    SVGs,
    Text,
    Video,
} from '@raskrask-dk/rr-components'
import {
    StyledIcons,
    StyledAddressStep,
    StyledAddressField,
    ErrorText,
} from './styles'
import { useTranslation } from 'react-i18next'
import { AddressTemplateProps, serviceIcons } from './props'
import { useAppSelector } from '../../redux/hooks'
import { getIsMobile } from '../../redux/reducers/appSettings'
import { PageTemplate } from '../chemistry/skeleton'

const AddressTemplate = ({
    onChangeAddress,
    onKeyDownAddress,
    onSubmitAddress,
    showError,
}: AddressTemplateProps): ReactElement => {
    const { t } = useTranslation()

    const videoRef = useRef<HTMLVideoElement>(null)

    const isMobile = useAppSelector((state) => getIsMobile(state))

    useEffect(() => {
        if (videoRef) {
            videoRef.current?.play()
        }
    }, [])

    /*------------Components------------*/
    const icons = useMemo(
        () => (
            <StyledIcons>
                {serviceIcons.map((icon, key) => (
                    <Image key={key} src={icon} />
                ))}
            </StyledIcons>
        ),
        []
    )

    const title = useMemo(
        () => (
            <Heading
                size={{ '@mobile': 5, '@desktop': 4 }}
                bold
                css={{ color: '$white', textAlign: 'center', zIndex: 3 }}
            >
                {t('address.title')}
            </Heading>
        ),
        [t]
    )

    const video = useMemo(
        () =>
            !isMobile && (
                <Video
                    autoPlay
                    controls={false}
                    loop
                    muted
                    playsInline
                    preload="auto"
                    ref={videoRef}
                    src="https://www.raskrask.dk/wp-content/themes/rask/Videos/massage_banner_wide_20s_HD_lq.mp4"
                />
            ),
        [isMobile]
    )

    const addressComponent = useMemo(
        () => (
            <StyledAddressField
                className="autocomplete-container"
                showError={showError}
            >
                <GreenInputField
                    buttonDataCy="addressBtn"
                    buttonDisabled={false}
                    buttonText={t('common:continue')}
                    data-cy="addressInput"
                    id="address-field"
                    onChange={onChangeAddress}
                    onClickButton={onSubmitAddress}
                    onKeyDown={onKeyDownAddress}
                    placeholder={t('address.input.placeholder')}
                    showButton
                />
                {showError && (
                    <ErrorText>
                        <SVG src={SVGs.Exclamation} />{' '}
                        <Text css={{ lineHeight: 1 }}>
                            {t('address.error')}
                        </Text>
                    </ErrorText>
                )}
            </StyledAddressField>
        ),
        [onChangeAddress, onKeyDownAddress, onSubmitAddress, showError, t]
    )

    return (
        <>
            <StyledAddressStep>
                {video}
                {icons}
                {title}
                {addressComponent}
            </StyledAddressStep>
            <PageTemplate
                Top={undefined}
                Body={undefined}
                Modal={undefined}
                hideBackButton
            />
        </>
    )
}

export default AddressTemplate
